/**
 * OpenTelemetry browser SDK bootstrap for the admin panel.
 *
 * Same shape as Dorat.Web.CustomerFacing's `app/lib/telemetry/otel.ts`.
 * When VITE_OTEL_ENDPOINT is unset or not a parseable absolute URL,
 * this module is a complete no-op — so missing config in any
 * environment (local dev included) never breaks the app.
 *
 * Auto-instrumentations enabled:
 * - document-load → first page load timing
 * - fetch          → every fetch() call as a span with trace context
 * - xhr            → axios / generated API client calls (axios uses XHR)
 *
 * Logs (errors mirrored from the Fuse ErrorBoundary) ship as OTel
 * LogRecords on the same provider. No metrics yet — once SigNoz
 * dashboards exist, web-vitals can be added the same way customer-web
 * does it.
 */

import { logs, SeverityNumber } from '@opentelemetry/api-logs';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';

const DEFAULT_SERVICE_NAME = 'dorat-admin';

let initialized = false;
let otelLogger: ReturnType<typeof logs.getLogger> | null = null;

function isValidAbsoluteHttpUrl(value: string | undefined): value is string {
	if (!value) return false;

	try {
		const url = new URL(value);
		return url.protocol === 'http:' || url.protocol === 'https:';
	} catch {
		return false;
	}
}

export function initOtel(): void {
	if (initialized) return;

	if (typeof window === 'undefined') return;

	const endpoint = import.meta.env.VITE_OTEL_ENDPOINT;

	if (!isValidAbsoluteHttpUrl(endpoint)) {
		return;
	}

	const authToken = import.meta.env.VITE_OTEL_AUTH_TOKEN;
	const headers: Record<string, string> = {};

	if (authToken) {
		headers['Authorization'] = `Bearer ${authToken}`;
	}

	const base = endpoint.replace(/\/+$/, '');
	const serviceName = import.meta.env.VITE_OTEL_SERVICE_NAME || DEFAULT_SERVICE_NAME;

	// Prefer an explicit VITE_OTEL_DEPLOYMENT_ENV (set per env in the
	// deploy workflows: "staging" / "production"). Fall back to Vite's
	// MODE for local dev (which gives "development").
	const deploymentEnv = import.meta.env.VITE_OTEL_DEPLOYMENT_ENV || import.meta.env.MODE;

	const resource = resourceFromAttributes({
		'service.name': serviceName,
		'deployment.environment': deploymentEnv
	});

	try {
		const tracerProvider = new WebTracerProvider({
			resource,
			spanProcessors: [
				new BatchSpanProcessor(
					new OTLPTraceExporter({
						url: `${base}/v1/traces`,
						headers
					})
				)
			]
		});
		tracerProvider.register();

		registerInstrumentations({
			instrumentations: [
				new DocumentLoadInstrumentation(),
				new FetchInstrumentation({
					propagateTraceHeaderCorsUrls: [/.*/]
				}),
				new XMLHttpRequestInstrumentation({
					propagateTraceHeaderCorsUrls: [/.*/]
				})
			]
		});

		const loggerProvider = new LoggerProvider({
			resource,
			processors: [
				new BatchLogRecordProcessor(
					new OTLPLogExporter({
						url: `${base}/v1/logs`,
						headers
					})
				)
			]
		});
		logs.setGlobalLoggerProvider(loggerProvider);
		otelLogger = logs.getLogger(serviceName);

		initialized = true;
	} catch (err) {
		// OTel must never crash the app.
		// eslint-disable-next-line no-console
		console.warn('[otel] Initialization failed; continuing without OTel:', err);
	}
}

export interface OtelErrorContext {
	componentStack?: string;
	source?: string;
	[key: string]: unknown;
}

/**
 * Emit an ERROR log record. No-op until initOtel() has completed
 * (and therefore until VITE_OTEL_ENDPOINT is a parseable URL).
 *
 * Accepts `unknown` because React 19's onUncaughtError / onCaughtError
 * give an `unknown` and forcing callers to narrow is friction at every
 * call site. We coerce here so the OTel log always has a string body.
 */
export function logErrorToOtel(error: unknown, context?: OtelErrorContext): void {
	if (!otelLogger) return;

	const e: Error | string =
		error instanceof Error ? error : typeof error === 'string' ? error : new Error(String(error));

	const message = typeof e === 'string' ? e : e.message;
	const stack = typeof e === 'string' ? undefined : e.stack;

	try {
		otelLogger.emit({
			severityNumber: SeverityNumber.ERROR,
			severityText: 'ERROR',
			body: message,
			attributes: {
				'exception.message': message,
				...(stack ? { 'exception.stacktrace': stack } : {}),
				...(context ?? {})
			}
		});
	} catch {
		// Never let logging crash anything.
	}
}
